import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
} from '@trialcard/apigateway.models';
import * as enums from '@trialcard/enums';

import moment, { Moment } from 'moment';
import { EdgeEnrollmentApi } from '@trialcard/apigateway.client';
import { AccountExternalIdType } from '@trialcard/enums';
import { UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel } from '@trialcard/apigateway.models/upsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Settings } from '~/services/settings';
import EligibilityModule from '~/store/eligibility';
import SurveyModule from '~/store/survey';

@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    @Resolve
    public eligibility!: EligibilityModule;

    alternativeEnrollment: boolean | null = null;

    medicalMemberNumber: string | null = '';
    pharmacyMemberNumber: string | null = '';

    defaultPatientApplicationData = {
        patientFirstName: '' as string,
        patientLastName: '' as string,
        patientMiddleInitial: '' as string,
        patientAddressOne: '' as string,
        patientAddressTwo: '' as string,
        city: '' as string,
        state: '' as string,
        zip: '' as string,
        patientDateOfBirth: null as Moment | string | null,
        gender: null as enums.Gender | null | string,
        patientPhoneNumber: '' as string,
        patientPhoneNumberPurpose: 0 as number,
        patientEmailAddress: '' as string,

        prescriberFirstName: '' as string,
        PrescriberLastName: '' as string,
        prescriberSiteName: '' as string,
        prescriberPhoneNumber: '' as string,
        prescriberFaxNumber: '' as string,
        prescriberState: '' as string,
        prescriberAddressOne: '' as string,
        prescriberAddressTwo: '' as string,
        prescriberCity: '' as string,
        prescriberZip: '' as string,
        ipsenCaresId: '' as string,
        guardianFirstName: '' as string,
        guardianLastName: '' as string,
    };

    defaultPayorObject =  {
        model: {
            payorId: (null as unknown) as number,
            payorType: (null as unknown) as number,
            accountRelationshipIsPrimary: true,
            payorGroup: '',
            payorName: '',
            webAddresses: [],
            addresses: [],
            phoneNumbers: [
                {
                    phoneType: 1,
                    phoneNumber: '',
                    phonePurpose: 1,
                },
                {
                    phoneType: 3,
                    phoneNumber: '',
                    phonePurpose: 8,
                },
            ],
            emailAddresses: [],
            contacts: [],
            payorBenefit: {
                dateOfBirth: null as Moment | null,
                policyHolderFirstName: '',
                policyHolderLastName: '',
                relationshipToPatient: undefined,
                policyNumber: '',
                planType: undefined,
            },
        },
    }
    defaultPayorData = [
    ] as UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[];


    patientApplicationData = { ...this.defaultPatientApplicationData };
    payorData = [ ...this.defaultPayorData ];
    expirationDate: moment.Moment | null = moment();
    alreadyEnrolled = false as boolean;

    @Mutation
    public async updatePatientApplication(newPatientApplication: typeof EnrollmentModule.prototype.patientApplicationData) {
        this.patientApplicationData = { ...this.patientApplicationData, ...newPatientApplication };
    }

    @Mutation
    public async updatePayors(newPayorData: UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[]) {
        this.payorData = [...newPayorData];
    }

    @Mutation
    public setMedicalMemberNumber(medical: string | null) {
        this.medicalMemberNumber = medical;
    }

    @Mutation
    public setPharmacyMemberNumber(pharmacy: string | null) {
        this.pharmacyMemberNumber = pharmacy;
    }

    @Mutation
    public setAlreadyEnrolled(newEnrollmentStatus: boolean) {
        this.alreadyEnrolled = newEnrollmentStatus;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment | null) {
        this.expirationDate = expirationDate;
    }

    @Action
    public updateAlreadyEnrolled(newEnrollmentStatus: boolean) {
        this.setAlreadyEnrolled(newEnrollmentStatus);
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientApplication({ ...this.defaultPatientApplicationData });
        this.updatePayors(this.defaultPayorData);
        this.updateAlreadyEnrolled(false);
        this.setMedicalMemberNumber(null);
        this.setPharmacyMemberNumber(null);
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        try {
            const patient = {
                account: {
                    sourceId: parseInt(this.settings.sourceId),
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientApplicationData,
                            firstName: this.patientApplicationData.patientFirstName,
                            lastName: this.patientApplicationData.patientLastName,
                            middleName: this.patientApplicationData.patientMiddleInitial,
                            phoneNumbers: [
                                this.structuredPhoneObjForPatient(
                                    this.patientApplicationData.patientPhoneNumber,
                                    this.patientApplicationData.patientPhoneNumberPurpose
                                ),
                            ] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
                            emailAddresses:
                                this.patientApplicationData.patientEmailAddress?.length > 0
                                    ? ([
                                          {
                                              emailAddressType: 0,
                                              address: this.patientApplicationData.patientEmailAddress,
                                          },
                                      ] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[])
                                    : [],
                            dateOfBirth: this.patientApplicationData.patientDateOfBirth,
                            addresses: [
                                {
                                    addressType: enums.AddressType.MAILINGADDRESS,
                                    addressOne: this.patientApplicationData.patientAddressOne,
                                    addressTwo: this.patientApplicationData.patientAddressTwo,
                                    city: this.patientApplicationData.city,
                                    state: this.patientApplicationData.state,
                                    zip: this.patientApplicationData.zip,
                                },
                            ] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            externalIds:
                                this.patientApplicationData.ipsenCaresId?.length > 0
                                    ? [
                                          {
                                              externalId: this.patientApplicationData.ipsenCaresId,
                                              externalIdType: AccountExternalIdType.THIRDPARTYID,
                                          },
                                      ]
                                    : [],
                            accountType: 1,
                            accountStatus: 1,
                            contacts:
                                this.patientApplicationData.guardianFirstName && this.patientApplicationData.guardianLastName
                                    ? [
                                          {
                                              roleType: enums.RoleType.LegalRepresentative,
                                              firstName: this.patientApplicationData.guardianFirstName,
                                              lastName: this.patientApplicationData.guardianLastName,
                                          },
                                      ]
                                    : ([] as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[]),
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    payorData: this.payorData,
                    prescriberData: [
                        {
                            canUpdate: false,
                            canInsert: true,
                            skipSearch: true,
                            model: {
                                accountRelationshipIsPrimary: true,
                                firstName: this.patientApplicationData.prescriberFirstName,
                                lastName: this.patientApplicationData.PrescriberLastName,
                                phoneNumbers:
                                    this.patientApplicationData.prescriberPhoneNumber.length > 0
                                        ? ([
                                              this.structuredPhoneObjForPrescriber(
                                                  this.patientApplicationData.prescriberPhoneNumber,
                                                  enums.ContactInfoType.PHONE
                                              ),
                                          ] as EnterpriseCoordinatorSharedPhoneUpsearchModel[])
                                        : [],
                            } as EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
                        } as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
                    ],
                    siteData: [
                        {
                            canUpdate: false,
                            canInsert: true,
                            skipSearch: true,
                            model: {
                                accountRelationshipIsPrimary: true,
                                siteRoleType: enums.RoleType.PrescriberSite,
                                description: this.patientApplicationData.prescriberSiteName,
                                pointOfContacts: [
                                    {
                                        preferredContactMethod: enums.ContactInfoType.FAX,
                                        phoneNumber:
                                            this.patientApplicationData.prescriberPhoneNumber.length > 0
                                                ? this.formatPhoneForAPI(this.patientApplicationData.prescriberPhoneNumber)
                                                : '',
                                        faxNumber: this.formatPhoneForAPI(this.patientApplicationData.prescriberFaxNumber),
                                    },
                                ],
                                phoneNumbers: [
                                    this.structuredPhoneObjForPrescriber(
                                        this.patientApplicationData.prescriberFaxNumber,
                                        enums.ContactInfoType.FAX
                                    ),
                                ],
                                addresses: [
                                    {
                                        addressOne: this.patientApplicationData.prescriberAddressOne,
                                        addressTwo: this.patientApplicationData.prescriberAddressTwo,
                                        city: this.patientApplicationData.prescriberCity,
                                        state: this.patientApplicationData.prescriberState,
                                        zip: this.patientApplicationData.prescriberZip,
                                    },
                                ],
                            } as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
                        },
                    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                caseInitiator: enums.EnrollPatientCaseInitiator.patient,
                accountAttestations: [
                    {
                        code: 'eServiceAttestion',
                        startDate: moment()
                    },
                    {
                        code: 'PatientCAPProgram',
                        startDate: moment()
                    }
                ],
            };
            const axios: AxiosInstance = (this.patientEnrollmentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: '/edge/patientEnrollment/enrollPatient',
                method: 'POST',
                data: patient,
                headers: {
                    'x-program-id': this.settings.programId!,
                },
            };
            await Promise.all([
                (this.patientEnrollmentApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
                (this.patientEnrollmentApi as any).authentications.Bearer.applyToRequest(axiosRequest),
                (this.patientEnrollmentApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            return await axios.request(axiosRequest);
        } catch {
            return;
        }
    }

    public structuredPhoneObjForPatient(phone: string, phonePurpose: number) {
        switch (phonePurpose) {
            case enums.ContactInfoPurposeType.MAIN:
                return {
                    phoneNumber: this.formatPhoneForAPI(phone),
                    phonePurpose: enums.ContactInfoPurposeType.MAIN,
                    phoneType: enums.ContactInfoType.MOBILE,
                };
            case enums.ContactInfoPurposeType.HOME:
                return {
                    phoneNumber: this.formatPhoneForAPI(phone),
                    phonePurpose: enums.ContactInfoPurposeType.HOME,
                    phoneType: enums.ContactInfoType.PHONE,
                };

            case enums.ContactInfoPurposeType.WORK:
                return {
                    phoneNumber: this.formatPhoneForAPI(phone),
                    phonePurpose: enums.ContactInfoPurposeType.WORK,
                    phoneType: enums.ContactInfoType.PHONE,
                };

            default:
                return;
        }
    }

    public structuredPhoneObjForPrescriber(phone: string, phoneType: number) {
        switch (phoneType) {
            case enums.ContactInfoType.FAX:
                return {
                    phoneNumber: this.formatPhoneForAPI(phone),
                    phonePurpose: enums.ContactInfoPurposeType.UNKNOWN,
                    phoneType: enums.ContactInfoType.FAX,
                };
            case enums.ContactInfoType.PHONE:
                return {
                    phoneNumber: this.formatPhoneForAPI(phone),
                    phonePurpose: enums.ContactInfoPurposeType.UNKNOWN,
                    phoneType: enums.ContactInfoType.PHONE,
                };
            default:
                return;
        }
    }

    formatPhoneForAPI(phoneNumber: string) {
        return phoneNumber?.replace(/\D/g, '');
    }
}
